import UpgradeBanner from "../components/UpgradeBanner";
import LoadingMessages from "../components/LoadingMessages";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuthStore } from "../store/authStore";
import { toast } from "sonner";
import {
  ArrowLeft, ChartLineUp, Plus, Trash, ArrowClockwise, Spinner, Warning
} from "@phosphor-icons/react";

const S = {
  bg:       '#0a0a0f',
  white:    '#13131f',
  border:   'rgba(124,58,237,0.2)',
  borderMd: 'rgba(124,58,237,0.4)',
  text:     '#e8e8f0',
  muted:    '#9090b0',
  hint:     '#5a5a7a',
  indigo:   '#7c3aed',
};

function PositionBadge({ position, previous }) {
  if (position === null || position === undefined) {
    return <span style={{ fontSize:12, padding:'3px 8px', borderRadius:5, background:S.bg, color:S.hint, fontWeight:600 }}>—</span>;
  }
  const color = position <= 3  ? { bg:'rgba(21,128,61,0.2)',  text:'#4ade80' }
              : position <= 10 ? { bg:'rgba(29,78,216,0.2)',  text:'#60a5fa' }
              : position <= 30 ? { bg:'rgba(161,98,7,0.2)',   text:'#fbbf24' }
              :                  { bg:'rgba(185,28,28,0.2)',  text:'#f87171' };
  let arrow = null;
  if (previous !== null && previous !== undefined) {
    const diff = previous - position;
    if (diff > 0) arrow = <span style={{ color:'#4ade80', fontSize:10, marginLeft:3, fontWeight:700 }}>▲{diff}</span>;
    else if (diff < 0) arrow = <span style={{ color:'#f87171', fontSize:10, marginLeft:3, fontWeight:700 }}>▼{Math.abs(diff)}</span>;
  }
  return (
    <span style={{ fontSize:12, padding:'3px 8px', borderRadius:5, background:color.bg, color:color.text, fontWeight:700 }}>
      #{position}{arrow}
    </span>
  );
}

function TrendSparkline({ history }) {
  if (!history || history.length < 2) return <span style={{ fontSize:11, color:S.hint }}>No data</span>;
  const vals = history.map(h => h.position).filter(p => p !== null);
  if (vals.length < 2) return <span style={{ fontSize:11, color:S.hint }}>No data</span>;
  const min = Math.min(...vals), max = Math.max(...vals), range = max - min || 1;
  const W = 60, H = 20;
  const pts = vals.map((v, i) => `${(i/(vals.length-1))*W},${H-((v-min)/range)*H}`).join(' ');
  const last = vals[vals.length-1], first = vals[0];
  const color = last < first ? '#4ade80' : last > first ? '#f87171' : S.hint;
  return (
    <svg width={W} height={H} style={{ display:'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function RankTracker() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [projects, setProjects]         = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [keywords, setKeywords]         = useState([]);
  const [rankCheck, setRankCheck]       = useState(null);
  const [overview, setOverview]         = useState(null);
  const [newKeywords, setNewKeywords]   = useState('');
  const [loading, setLoading]           = useState(false);
  const [checking, setChecking]         = useState(false);
  const [adding, setAdding]             = useState(false);

  const plan = user?.plan || 'free';
  const maxKw = plan === 'agency' ? 2000 : plan === 'pro' ? 500 : 5;

  useEffect(() => {
    apiClient.get('/projects').then(p => {
      setProjects(p);
      if (p.length > 0) setSelectedProject(p[0].project_id);
    }).catch(console.error);
  }, []);

  const loadKeywords = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const [kws, ov, rc] = await Promise.all([
        apiClient.get(`/rank-tracker/keywords/${selectedProject}`),
        apiClient.get(`/rank-tracker/overview/${selectedProject}`).catch(() => null),
        apiClient.get(`/rank-tracker/check/${selectedProject}/status`).catch(() => null),
      ]);
      setKeywords(kws || []); setOverview(ov || null); setRankCheck(rc);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedProject]);

  useEffect(() => { loadKeywords(); }, [loadKeywords]);

  const handleAdd = async () => {
    const lines = newKeywords.split('\n').map(l => l.trim()).filter(Boolean);
    if (!lines.length || !selectedProject) return;
    if (keywords.length + lines.length > maxKw) { toast.error(`Plan limit: ${maxKw} keywords max`); return; }
    setAdding(true);
    try {
      await apiClient.post('/rank-tracker/keywords', { project_id: selectedProject, keywords: lines });
      setNewKeywords(''); await loadKeywords();
      toast.success(`${lines.length} keyword${lines.length > 1 ? 's' : ''} added`);
    } catch(e) { toast.error('Failed to add keywords'); }
    finally { setAdding(false); }
  };

  const handleDelete = async (kwId) => {
    try {
      await apiClient.fetch(`/rank-tracker/keywords`, { method:'DELETE', body:JSON.stringify({ keyword_ids:[kwId] }) });
      setKeywords(prev => prev.filter(k => k.keyword_id !== kwId));
      toast.success('Keyword removed');
    } catch(e) { toast.error('Failed to delete'); }
  };

  const handleCheck = async () => {
    if (!selectedProject || checking) return;
    setChecking(true);
    toast.success('Position check started...');
    try {
      await apiClient.post(`/rank-tracker/check/${selectedProject}`, {});
      setTimeout(async () => { await loadKeywords(); setChecking(false); toast.success('Positions updated!'); }, keywords.length * 3500 + 2000);
    } catch(e) { toast.error('Check failed'); setChecking(false); }
  };

  const stats = {
    total:    overview?.total_keywords ?? keywords.length,
    top3:     keywords.filter(k => k.current_position <= 3).length,
    top10:    keywords.filter(k => k.current_position > 3 && k.current_position <= 10).length,
    top30:    keywords.filter(k => k.current_position > 10 && k.current_position <= 30).length,
    notFound: keywords.filter(k => !k.current_position).length,
    improved: keywords.filter(k => k.previous_position && k.current_position && k.current_position < k.previous_position).length,
    declined: keywords.filter(k => k.previous_position && k.current_position && k.current_position > k.previous_position).length,
  };

  const card = { background:S.white, border:`1px solid ${S.border}`, borderRadius:10 };

  return (
    <div style={{ minHeight:'100vh', background:S.bg, fontFamily:"'IBM Plex Sans', system-ui, sans-serif", color:S.text }}>
      <header style={{ background:S.white, borderBottom:`1px solid ${S.border}`, position:'sticky', top:0, zIndex:50 }}>
        <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 24px', height:52, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <button onClick={() => navigate('/dashboard')}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', background:'transparent', border:`1px solid ${S.border}`, borderRadius:6, fontSize:13, color:S.muted, cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.borderColor=S.borderMd}
              onMouseLeave={e=>e.currentTarget.style.borderColor=S.border}>
              <ArrowLeft style={{ width:13, height:13 }} /> Dashboard
            </button>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:28, height:28, background:'rgba(124,58,237,0.15)', borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <ChartLineUp weight="bold" style={{ width:14, height:14, color:S.indigo }} />
              </div>
              <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:16, fontWeight:800, color:S.text, margin:0, letterSpacing:'-0.02em' }}>Rank Tracker</h1>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {projects.length > 0 && (
              <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
                style={{ padding:'5px 10px', border:`1px solid ${S.border}`, borderRadius:6, fontSize:13, color:S.text, background:S.white, cursor:'pointer', outline:'none' }}>
                {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
              </select>
            )}
            <button onClick={handleCheck} disabled={checking || !keywords.length}
              style={{ display:'flex', alignItems:'center', gap:7, padding:'7px 14px', background:checking?S.hint:S.indigo, color:'#fff', border:'none', borderRadius:7, fontSize:13, fontWeight:600, cursor:checking?'not-allowed':'pointer' }}>
              {checking ? <Spinner style={{ width:13, height:13 }} className="animate-spin" /> : <ArrowClockwise weight="bold" style={{ width:13, height:13 }} />}
              {checking ? 'Checking...' : 'Check positions'}
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth:1280, margin:'0 auto', padding:'32px 24px' }}>
        <UpgradeBanner feature="500 keywords, vérifications quotidiennes, alertes email" />

        <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:10, marginBottom:24 }}>
          {[
            { label:'Total keywords', value:stats.total,    color:S.text },
            { label:'Top 3',          value:stats.top3,     color:'#4ade80' },
            { label:'Top 10',         value:stats.top10,    color:'#60a5fa' },
            { label:'Top 30',         value:stats.top30,    color:'#fbbf24' },
            { label:'Not found',      value:stats.notFound, color:S.hint },
            { label:'Improved',       value:stats.improved, color:'#4ade80' },
            { label:'Declined',       value:stats.declined, color:'#f87171' },
          ].map(s => (
            <div key={s.label} style={{ ...card, padding:'14px 16px', textAlign:'center' }}>
              <p style={{ fontSize:22, fontWeight:900, color:s.color, margin:0, letterSpacing:'-0.03em', fontFamily:"'Satoshi', sans-serif" }}>{s.value}</p>
              <p style={{ fontSize:11, color:S.hint, margin:'3px 0 0', fontWeight:500 }}>{s.label}</p>
            </div>
          ))}
        </div>

        {checking && <LoadingMessages style={{ marginBottom: 20 }} />}

        <div style={{ display:'grid', gridTemplateColumns:'1fr 280px', gap:20, alignItems:'start' }}>
          <div style={{ ...card, overflow:'hidden' }}>
            <div style={{ padding:'14px 18px', borderBottom:`1px solid ${S.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <p style={{ fontSize:13, fontWeight:700, color:S.text, margin:0 }}>
                Keywords <span style={{ fontSize:12, fontWeight:500, color:S.hint, marginLeft:6 }}>{keywords.length}/{maxKw}</span>
              </p>
              {rankCheck?.completed_at && (
                <span style={{ fontSize:11, color:S.hint }}>Last check: {new Date(rankCheck.completed_at).toLocaleString('fr', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}</span>
              )}
            </div>
            {loading ? (
              <div style={{ padding:48, textAlign:'center' }}>
                <Spinner style={{ width:20, height:20, color:S.indigo }} className="animate-spin" />
              </div>
            ) : keywords.length === 0 ? (
              <div style={{ padding:'40px 24px', textAlign:'center' }}>
                <ChartLineUp style={{ width:28, height:28, color:S.border, margin:'0 auto 10px' }} />
                <p style={{ fontSize:13, color:S.hint, margin:0 }}>No keywords yet — add some on the right</p>
              </div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:S.bg }}>
                    {['Keyword','Position','Best','Trend (14d)','URL found',''].map(h => (
                      <th key={h} style={{ padding:'8px 16px', fontSize:11, fontWeight:700, color:S.hint, textAlign:h===''?'right':'left', letterSpacing:'0.05em', textTransform:'uppercase', borderBottom:`1px solid ${S.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {keywords.map((kw, i) => (
                    <tr key={kw.keyword_id}
                      style={{ borderBottom:i < keywords.length-1 ? `1px solid ${S.border}` : 'none' }}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(124,58,237,0.05)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{ padding:'12px 16px', fontSize:13, fontWeight:600, color:S.text }}>{kw.keyword}</td>
                      <td style={{ padding:'12px 16px' }}><PositionBadge position={kw.current_position} previous={kw.previous_position} /></td>
                      <td style={{ padding:'12px 16px', fontSize:12, color:S.muted }}>{kw.best_position ? `#${kw.best_position}` : '—'}</td>
                      <td style={{ padding:'12px 16px' }}><TrendSparkline history={kw.position_history} /></td>
                      <td style={{ padding:'12px 16px', fontSize:11, color:S.hint, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {kw.url_found ? kw.url_found.replace(/^https?:\/\//, '') : '—'}
                      </td>
                      <td style={{ padding:'12px 16px', textAlign:'right' }}>
                        <button onClick={() => handleDelete(kw.keyword_id)}
                          style={{ padding:4, background:'transparent', border:'none', cursor:'pointer', color:S.hint, borderRadius:4 }}
                          onMouseEnter={e=>e.currentTarget.style.color='#f87171'}
                          onMouseLeave={e=>e.currentTarget.style.color=S.hint}>
                          <Trash style={{ width:13, height:13 }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ ...card, padding:'16px 18px' }}>
              <p style={{ fontSize:13, fontWeight:700, color:S.text, margin:'0 0 4px', letterSpacing:'-0.01em' }}>Add keywords</p>
              <p style={{ fontSize:12, color:S.hint, margin:'0 0 12px' }}>{keywords.length}/{maxKw} used · one per line</p>
              <textarea value={newKeywords} onChange={e=>setNewKeywords(e.target.value)}
                placeholder={"seo audit tool\neasy gourmet recipes\nbest chicken soup recipe"}
                style={{ width:'100%', height:140, padding:'10px 12px', background:S.bg, border:`1px solid ${S.border}`, borderRadius:7, fontSize:13, color:S.text, fontFamily:'inherit', resize:'none', outline:'none', boxSizing:'border-box', lineHeight:1.6 }}
                onFocus={e=>e.target.style.borderColor=S.indigo}
                onBlur={e=>e.target.style.borderColor=S.border}
              />
              <button onClick={handleAdd} disabled={adding || !newKeywords.trim() || !selectedProject}
                style={{ width:'100%', marginTop:10, padding:'9px 0', background:adding?S.hint:S.indigo, color:'#fff', border:'none', borderRadius:7, fontSize:13, fontWeight:600, cursor:adding?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:7 }}>
                {adding ? <Spinner style={{ width:13, height:13 }} className="animate-spin" /> : <Plus weight="bold" style={{ width:13, height:13 }} />}
                {adding ? 'Adding...' : 'Add keywords'}
              </button>
            </div>

            <div style={{ ...card, padding:'14px 16px' }}>
              <p style={{ fontSize:11, fontWeight:700, color:S.hint, margin:'0 0 10px', letterSpacing:'0.06em', textTransform:'uppercase' }}>Tips</p>
              <ul style={{ margin:0, padding:'0 0 0 14px', fontSize:12, color:S.muted, lineHeight:1.8 }}>
                <li>Use long-tail keywords (3+ words)</li>
                <li>Include your city + service</li>
                <li>Add your brand name</li>
                <li>Checks run every night at 3AM</li>
              </ul>
            </div>

            {plan === 'free' && (
              <div style={{ background:'rgba(124,58,237,0.12)', border:'1px solid rgba(124,58,237,0.35)', borderRadius:10, padding:'14px 16px' }}>
                <p style={{ fontSize:12, fontWeight:700, color:'#a78bfa', margin:'0 0 4px' }}>Free plan: {maxKw} keywords</p>
                <p style={{ fontSize:11, color:S.indigo, margin:'0 0 10px' }}>Upgrade to Pro for 500 keywords</p>
                <button onClick={() => navigate('/pricing')}
                  style={{ width:'100%', padding:'7px 0', background:S.indigo, color:'#fff', border:'none', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  Upgrade to Pro →
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
